import { Property } from 'csstype';
import { Styles as ReactSelectStyles } from 'react-select/src/styles';
import { colors as componentLibraryColors } from '@goshippo/components';

export { componentLibraryColors };

export const fontFamily =
  "neue-haas-unica, pragmatica-web, 'Helvetica Neue', Helvetica, Arial, sans-serif";

export const leftNavWidth = '240px';
export const leftNavTabletWidth = '64px';

// Feron V3 color update
// Palette comes from
// https://www.figma.com/file/8hviGiukYiE00M3DZCQSUl/Shippo-Brand-Colors-3.0-Final?

export const v3OldColors = {
  white: '#FFFFFF',
  text: {
    primary: {
      white: '#FFFFFF',
      black: '#1B1D1D',
      blue: '#00AFF3', /// cyan 50
      green: '#A0D249', /// northstar 50
    },
  },

  royalOrange70: '#CA4F02',

  orchid70: '#A100C1',

  gray10: '#F7FBFC',
  gray20: '#E9F1F5',
  gray30: '#CFDEE6',
  gray40: '#B8CED9',
  gray50: '#7B99A8',
  gray60: '#5C7B8A',
  gray70: '#4C6774',
  gray80: '#425B66',
  gray90: '#2E434D',
  gray100: '#1F2D34',
};

// Scaled Colors
// Shippo Design System 2.2020
// https://www.figma.com/file/6bmZ29sLXCplMNzaZ2z7O7/Shippo-Design-System?node-id=68%3A0
const designSystemColors = {
  forest50: componentLibraryColors.accent.background.success,
  forest100: componentLibraryColors.accent.background.success,
  forest200: componentLibraryColors.accent.background.success,
  forest600: componentLibraryColors.accent.foreground.success,
  forest700: componentLibraryColors.accent.foreground.success,
  forest800: componentLibraryColors.primaryButtonHover,

  gray10: v3OldColors.gray10,
  gray25: v3OldColors.gray20,
  gray50: v3OldColors.gray20,
  gray100: v3OldColors.gray10,
  gray200: v3OldColors.gray20,
  gray300: v3OldColors.gray30,
  gray400: v3OldColors.gray40,
  gray500: v3OldColors.gray50,
  gray600: v3OldColors.gray60,
  gray700: v3OldColors.gray70,
  gray800: v3OldColors.gray80,
  gray900: v3OldColors.gray90,
  gray1000: v3OldColors.gray100,

  sea25: componentLibraryColors.secondaryHover,
  sea50: componentLibraryColors.backgroundDeemphasized,
  sea100: componentLibraryColors.secondary,
  sea200: componentLibraryColors.secondary,
  sea300: componentLibraryColors.secondary,
  sea400: componentLibraryColors.secondary,
  sea500: componentLibraryColors.secondary,
  sea600: componentLibraryColors.secondary,
  sea700: componentLibraryColors.secondary,
  sea800: componentLibraryColors.secondary,
  sea900: componentLibraryColors.secondary,

  ruby50: componentLibraryColors.accent.background.error,
  ruby400: componentLibraryColors.accent.foreground.error,
  ruby600: componentLibraryColors.accent.foreground.error,
  ruby700: componentLibraryColors.accent.foreground.error,

  macaroni50: componentLibraryColors.accent.background.warning,
  macaroni100: componentLibraryColors.accent.background.warning,
  macaroni300: componentLibraryColors.accent.background.warning,
  macaroni600: componentLibraryColors.accent.foreground.warning,
  macaroni700: componentLibraryColors.accent.foreground.warning,
  macaroni800: componentLibraryColors.accent.foreground.warning,
  macaroni900: componentLibraryColors.accent.foreground.warning,

  white: '#FFFFFF',
  black: '#1B1D1D',
};

export const colors = {
  ...designSystemColors,

  borders: componentLibraryColors.borders,
  primary: componentLibraryColors.accent.foreground.success,
  primaryFocus: componentLibraryColors.primaryButtonHover,
  primaryAlternative: componentLibraryColors.primaryButtonHover,
  secondary: componentLibraryColors.secondary,
  tertiary: componentLibraryColors.secondaryHover,
  dangerRed: componentLibraryColors.accent.foreground.error,
  getStartedFooterGrey: componentLibraryColors.secondaryHover,
  getStartedFooterBorder: componentLibraryColors.backgroundDeemphasized,
  containerBorderColor: componentLibraryColors.borders, // the container or section separations should use this color as border.

  // 2020 Colors (not from new design system)
  // Temporarily using this to name colors until we use the des. sys. color gradient naming convention
  // http://chir.ag/projects/name-that-color
  geyser: '#cfdce2',
  rollingStone: '#718087',

  // Upsell colors
  coolGray: '#678096',

  fontColor: '#333333',
  placeholderColor: '#AEAEAE',
  disabledIcon: '#e3e4e5',

  greyBorder: componentLibraryColors.borders,

  // TODO: Colors that used to come from shippo-frontend variables, review with Jason
  viking: '#50C1DB',
  emperor: '#555555', // Same as form.labelFontColor, ex matterhornGray
  nightRider: '#333333', // Ex mineShaft2, same as form.fontColor
  loblolly: componentLibraryColors.borders, // Same as tags.highlighted.borderColor, also used in left nav
  aqua: '#31A2C4',
  almostWhite: '#F1F5F7',
  borderColor: '#ddd',

  // Error colors
  errorRed: componentLibraryColors.accent.foreground.error,
  errorShadowRed: componentLibraryColors.accent.background.error,

  // TODO: Pagination colors, not final
  pagination: {
    hoverBackground: componentLibraryColors.backgroundDeemphasized,
    borderColor: componentLibraryColors.borders,
    activeBorder: componentLibraryColors.accent.foreground.success,
  },

  form: {
    greyBorder: componentLibraryColors.borders,
    errorBar: componentLibraryColors.accent.foreground.error,
    errorBorder: componentLibraryColors.accent.foreground.error,
    errorBackground: componentLibraryColors.accent.background.error,
    warningBackground: componentLibraryColors.accent.background.warning,
    warningBorder: componentLibraryColors.accent.background.warning,
    successBar: componentLibraryColors.accent.background.success,
    successBorder: componentLibraryColors.accent.background.success,
    successBackground: designSystemColors.gray25,
    greyDisabled: componentLibraryColors.secondaryHover,
    greyDisabledBorder: componentLibraryColors.backgroundDeemphasized,
    greyDisabledButton: componentLibraryColors.backgroundDeemphasized,
    placeholderFontColor: componentLibraryColors.borders,
    fontColor: componentLibraryColors.text.primary.black,
    labelFontColor: designSystemColors.black,
    disabledLabelFontColor: componentLibraryColors.borders,
    shadow: componentLibraryColors.borders,
    activeBorderColor: componentLibraryColors.borders,
  },

  tags: {
    removeIconColor: '#979797',
    hoverRemoveIconColor: '#555555',

    highlighted: {
      borderColor: '#B9C7CE',
      backgroundColor: '#F4F9FB',
    },
  },

  dialog: {
    errorBackground: componentLibraryColors.accent.background.error,
    successBackground: componentLibraryColors.accent.background.success,
    infoBackground: componentLibraryColors.secondaryHover,
  },

  banner: {
    info: '#F2FBFF',
  },

  notification: {
    boxShadow: 'rgba(0, 0, 0, 0.12)',
  },

  branding: {
    defaultBrandPrimaryColor: '#000000',
    defaultBrandSecondaryColor: '#F1F1F1',
  },
};

export type LeftNavTheme = {
  logo: string;
  backAndClose: string;
  text: string;
  background: string;
  backgroundActive: string;
  border: string;
  highlight: string;
  submenuBackground: string;
  submenuBorder: string;
  submenuLink: string;
  mobileSubmenuLink: string;
  submenuActive: string;
  mobileSubmenuActive: string;
  title: string;
  mobileTitle: string;
  sectionTitle: string;
  mobileSectionTitle: string;
};

export const leftNavThemes: { [key: string]: LeftNavTheme } = {
  default: {
    logo: '#98BF50',
    backAndClose: colors.gray600,
    text: colors.rollingStone,
    background: colors.gray10,
    backgroundActive: '#e3e9ec',
    border: 'rgba(0, 0, 0, 0.05)',
    highlight: colors.primary,
    submenuBackground: colors.gray10,
    submenuBorder: 'rgba(0, 0, 0, 0.05)',
    submenuLink: colors.rollingStone,
    mobileSubmenuLink: colors.rollingStone,
    submenuActive: colors.primary,
    mobileSubmenuActive: colors.primary,
    title: colors.gray600,
    mobileTitle: colors.gray600,
    sectionTitle: colors.loblolly,
    mobileSectionTitle: colors.loblolly,
  },
  alternate: {
    logo: colors.white,
    backAndClose: colors.white,
    text: colors.white,
    background: '#3373BE',
    backgroundActive: '#2D67AA',
    border: 'transparent',
    highlight: '#3373BE',
    submenuBackground: colors.gray10,
    submenuBorder: 'rgba(0, 0, 0, 0.05)',
    submenuLink: colors.rollingStone,
    mobileSubmenuLink: colors.white,
    submenuActive: '#3373BE',
    mobileSubmenuActive: colors.white,
    title: colors.gray600,
    mobileTitle: colors.white,
    sectionTitle: colors.loblolly,
    mobileSectionTitle: colors.white,
  },
};

export const formSizes = {
  inputHeight: '2.5rem',
  inputWidth: '15rem',
};

export const ordersTableCellMinHeight = '4.5rem';

export const zIndexValues = {
  base: 1,
  leftColumn: 2,
  orderBatchMenu: 10,
  dropdown: 15,
  anchoredDiv: 98,
  overlay: 99,
  multiSelect: 100,
  modal: 101,
  persistantBanner: 999,
  banner: 1000,
  tooltip: 1070,
};

export const fontWeight: Record<string, Property.FontWeight> = {
  thin: 100,
  extraLight: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
  black: 900,
};

export const classNames = {
  fulfilledOrderText: 'fulfilled-order-text', // targeted by orders table to set additional styles in cells based on order fulfillment status
};

const getSelectOptionBackgroundColor = (isSelected?: boolean, isFocused?: boolean) => {
  if (isSelected) return colors.white;
  if (isFocused) return colors.gray25;
  return 'inherit';
};

export const reactSelectDropdownStyles: ReactSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: '4px',
    border: state.isFocused
      ? `1px solid ${colors.form.activeBorderColor}`
      : `1px solid ${colors.form.greyBorder}`,
    color: `${componentLibraryColors.text.primary.black}`,
    borderColor:
      (state.isFocused ? colors.form.activeBorderColor : colors.form.greyBorder) + ' !important',
    minHeight: '2.5rem',
    height: '2.5rem',
    boxShadow: 'none',
    borderBottomLeftRadius:
      state.menuIsOpen && state.selectProps.menuPlacement !== 'top' ? 0 : '4px',
    borderBottomRightRadius:
      state.menuIsOpen && state.selectProps.menuPlacement !== 'top' ? 0 : '4px',
    borderTopLeftRadius:
      state.menuIsOpen && state.selectProps.menuPlacement !== 'bottom' ? 0 : '4px',
    borderTopRightRadius:
      state.menuIsOpen && state.selectProps.menuPlacement !== 'bottom' ? 0 : '4px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  container: (provided) => ({
    ...provided,
    padding: 0,
    margin: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 0.75rem',
    fontWeight: 'normal',
    height: '2.5rem',
    overflow: 'initial',
  }),
  option: (provided, state) => ({
    ...provided,
    position: 'relative',
    cursor: 'pointer',
    padding: '0.5rem 1rem',
    color: `${colors.form.fontColor}`,
    backgroundColor: getSelectOptionBackgroundColor(state.isSelected, state.isFocused),
    '&:hover': {
      backgroundColor: `${componentLibraryColors.secondaryHover}`,
    },
    fontSize: '0.875rem',
    fontWeight: state.isSelected ? fontWeight.medium : fontWeight.normal,
  }),
  clearIndicator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '.5rem',
    color: `${componentLibraryColors.secondary}`,
    transform: state.selectProps.menuIsOpen && 'rotate(180deg)',
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0rem',
    backgroundColor: state.pasteEdited ? `${colors.forest50}` : '',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: `${componentLibraryColors.secondary}`,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '2.5rem',
  }),
  menu: (provided, state) => ({
    ...provided,
    marginTop: 0,
    marginBottom: state.selectProps.menuPlacement !== 'top' ? 0 : '',
    borderTopLeftRadius: state.selectProps.menuPlacement !== 'top' ? 0 : '',
    borderTopRightRadius: state.selectProps.menuPlacement !== 'top' ? 0 : '',
    borderBottomLeftRadius: state.selectProps.menuPlacement !== 'bottom' ? 0 : '',
    borderBottomRightRadius: state.selectProps.menuPlacement !== 'bottom' ? 0 : '',
    boxShadow:
      state.selectProps.menuPlacement !== 'top' ? '0px 6px 20px rgba(0, 0, 0, 0.15)' : 'none',
    border: `1px solid ${componentLibraryColors.secondary}`,
    boxSizing: 'border-box',
    borderTop: state.selectProps.menuPlacement !== 'top' ? 'none' : '',
    borderBottom: state.selectProps.menuPlacement !== 'bottom' ? 'none' : '',
  }),
};
